import React, { useState } from 'react';
import { Alert, Button, InputNumber, Modal, message } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { useUpdateTeam } from '../../hooks/useUpdateTeam';

type Props = {
  memberCap?: number;
  teamId: number;
  sfAccountId?: string;
  refetchTeam: () => void;
  disabled?: boolean;
};

const ContractSeats = ({
  memberCap,
  teamId,
  sfAccountId,
  refetchTeam,
  disabled,
}: Props) => {
  const [isModalVisible, setModalVisible] = useState(false);
  const [seats, setSeats] = useState<number | null>(memberCap ?? null);

  const { onSubmit, isLoading } = useUpdateTeam(refetchTeam);

  const handleOpenModal = () => {
    setSeats(memberCap ?? null);
    setModalVisible(true);
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    setSeats(memberCap ?? null);
  };

  const handleSave = async () => {
    if (seats === null) {
      return;
    }

    try {
      await onSubmit({ member_cap: seats }, teamId);
      message.success('Success! The contract seats have been updated.');
      refetchTeam();
      setModalVisible(false);
    } catch {
      message.error('Failed to update the contract seats. Please try again.');
    }
  };

  const isUnchanged = seats === (memberCap ?? null);
  const isInvalid = seats === null || seats < 0;

  return (
    <>
      <span>{memberCap}</span>
      <div className="pr-2">
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={handleOpenModal}
          disabled={disabled}
          aria-label="Edit contract seats"
        />
      </div>

      <Modal
        title="Edit Contract Seats"
        open={isModalVisible}
        onCancel={handleCloseModal}
        onOk={handleSave}
        okText="Save"
        okButtonProps={{
          disabled: isUnchanged || isInvalid || isLoading,
          loading: isLoading,
        }}
        cancelButtonProps={{ disabled: isLoading }}
      >
        <InputNumber
          className="w-full"
          min={0}
          precision={0}
          value={seats}
          onChange={(value) => setSeats(value === null ? null : Number(value))}
          placeholder="Enter the number of contract seats"
        />

        {!!sfAccountId && (
          <Alert
            className="mt-4"
            type="warning"
            showIcon
            message="Salesforce is the source of truth for this value. Any changes you make will be overwritten on the next nightly Salesforce sync cycle."
          />
        )}
      </Modal>
    </>
  );
};

export default ContractSeats;
